import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import { capitalize, get, set } from 'lodash';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import {
  NAME_REGEX,
  USA_SOCIAL_SECURITY_NUMBER_REGEX,
  getPostcodeExample,
  isValidChinaBankCardNumber,
  isValidNationalId,
  isValidPostcode,
} from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import {
  countryOptions,
  getLocalityExample,
  getNationalIdDetails,
  isIndividualCountryGroup1,
  isIndividualCountryGroup3,
  isIndividualCountryWithLocality,
  isIndividualCountryWithNationalId,
} from '@/modules/entity/static/countries.ts';
import { genderOptions } from '@/modules/entity/static/gender-options.ts';
import { localityOptions } from '@/modules/entity/static/locality.ts';
import { streetTypeOptions, streetTypeOptionsUSA } from '@/modules/entity/static/street-types.ts';
import { titleOptions } from '@/modules/entity/static/titles.ts';
import { usaStateOptions } from '@/modules/entity/static/usa-states.ts';
import { ChangeShareholderComponentProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { CalendarInput, Select, TextInputField } from 'ui';

interface PersonIndividualDetailsFormik {
  first_name: string;
  last_name: string;
  title: string;
  middle_name: string;
  gender: string;
  date_of_birth: string;
  country: string;
  unit_number: string;
  street_number: string;
  street_name: string;
  street_type: string;
  locality: string;
  suburb: string;
  state: string;
  postcode: string;
  social_security_number: string;
  province: string;
  national_id: string;
  complete_name: string;
  complete_name_chinese: string;
  address_line_1: string;
  bank_card_number: string;
}

const validationSchema = yup.object({
  gender: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN'
        ? schema
            .oneOf(['male', 'female'], 'Select Male or Female')
            .required('This field is required')
        : schema,
    ),
  first_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.optional() : schema.required('This field is required'),
    ),
  last_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.optional() : schema.required('This field is required'),
    ),
  title: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.optional() : schema.required('This field is required'),
    ),
  middle_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .nullable()
    .optional(),
  date_of_birth: yup.string().trim().required('This field is required'),
  country: yup.string().trim().required('This field is required'),
  unit_number: yup.string().trim(),
  street_number: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.optional() : schema.required('This field is required'),
    ),
  street_name: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.optional() : schema.required('This field is required'),
    ),
  street_type: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'AUS' ? schema.required('This field is required') : schema.optional(),
    ),
  locality: yup
    .string()
    .trim()
    .when('country', (field, schema) => {
      if (isIndividualCountryWithLocality(field[0])) {
        return schema.required('This field is required');
      }

      return schema;
    }),
  suburb: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.optional() : schema.required('This field is required'),
    ),
  state: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS') {
        return schema.required('This field is required');
      }

      return schema.nullable();
    }),
  social_security_number: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'USA'
        ? schema.matches(USA_SOCIAL_SECURITY_NUMBER_REGEX, {
            message: 'Invalid Social Security Number format, e.g. 123-45-6789',
            excludeEmptyString: true,
          })
        : schema,
    ),
  province: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.required('This field is required') : schema.optional(),
    ),
  national_id: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      isIndividualCountryWithNationalId(country)
        ? schema.test('national-id-format', function validateNationalId(value) {
            if (!value || isValidNationalId(this.parent.country, value)) return true;

            const details = getNationalIdDetails(this.parent.country);
            return this.createError({
              message: `Invalid ${details.label} format. Enter ${details.formatHint}`,
            });
          })
        : schema.optional(),
    ),
  complete_name: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.required('This field is required') : schema.optional(),
    ),
  complete_name_chinese: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.required('This field is required') : schema.optional(),
    ),
  address_line_1: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN' ? schema.required('This field is required') : schema.optional(),
    ),
  bank_card_number: yup
    .string()
    .trim()
    .when('country', ([country], schema) =>
      country === 'CHN'
        ? schema
            .test(
              'bank-card-number-format',
              'Invalid bank card number',
              value => !!value && isValidChinaBankCardNumber(value),
            )
            .required('This field is required')
        : schema.optional(),
    ),
  postcode: yup
    .string()
    .trim()
    .test('postcode-format', 'Invalid postcode format', function validatePostcode(value) {
      return !value || isValidPostcode(this.parent.country, value);
    })
    .required('This field is required'),
});

const initialValues: PersonIndividualDetailsFormik = {
  first_name: '',
  last_name: '',
  title: '',
  middle_name: '',
  gender: '',
  date_of_birth: '',
  country: '',
  unit_number: '',
  street_number: '',
  street_name: '',
  street_type: '',
  suburb: '',
  state: '',
  postcode: '',
  social_security_number: '',
  locality: '',
  province: '',
  national_id: '',
  complete_name: '',
  complete_name_chinese: '',
  address_line_1: '',
  bank_card_number: '',
};

export const PersonIndividualDetails = ({
  index,
  title,
  moveForward,
  entity_type_prefix,
  person_details_key,
  allowSkip,
  titleHint,
}: ChangeShareholderComponentProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));
  const { data: currentUser } = useCurrentUserQuery();

  const generateInitialValues = (): PersonIndividualDetailsFormik => {
    if (person_details_key) {
      return {
        ...initialValues,
        ...get(data?.entity, `${entity_type_prefix}.${person_details_key}.${index}`, {}),
        country:
          get(data?.entity, `${entity_type_prefix}.${person_details_key}.${index}`, {})?.country ||
          get(data?.entity, entity_type_prefix)?.country ||
          data?.entity?.country,
      };
    }

    return {
      ...initialValues,
      country: data?.entity?.country || initialValues.country,
      first_name: currentUser?.first_name,
      last_name: currentUser?.last_name,
      ...get(data?.entity, entity_type_prefix),
    };
  };

  const generatePayload = (values: PersonIndividualDetailsFormik) => {
    const address =
      values.country === 'CHN'
        ? `${values.address_line_1 ?? ''}, ${values.locality ?? ''} ${values.province ?? ''}`.trim()
        : `${values.street_number ?? ''} ${values.street_name ?? ''} ${values.street_type ?? ''}, ${
            values.suburb ?? ''
          } ${values.state ?? ''}`.trim();

    if (person_details_key) {
      const subEntities = get(data?.entity, `${entity_type_prefix}.${person_details_key}`, []);
      return set(
        { ...data?.entity, address },
        `${entity_type_prefix}.${person_details_key}`,
        subEntities.map((subEntity: any, indexOfSubEntity: number) =>
          indexOfSubEntity === index
            ? {
                ...subEntity,
                ...values,
                address,
                middle_name: values.middle_name || null,
              }
            : subEntity,
        ),
      );
    }
    return set(
      {
        ...data?.entity,
        address,
      },
      entity_type_prefix,
      {
        ...get(data?.entity, entity_type_prefix),
        ...values,
        middle_name: values.middle_name || null,
        address,
      },
    );
  };

  const formik = useFormik<PersonIndividualDetailsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(),
    enableReinitialize: true,
    onSubmit: async values => {
      if (formik.dirty) {
        await updateEntity(generatePayload(values));
      }
      moveForward && moveForward();
    },
  });
  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeSelect = (field: string, value: string) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    if (field === 'country') {
      if (value !== 'USA') {
        formik.setFieldValue('social_security_number', '');
        formik.setFieldError('social_security_number', '');
      }
      formik.setFieldValue('state', '');
      formik.setFieldError('state', '');
      formik.setFieldValue('street_type', '');
      formik.setFieldError('street_type', '');
      formik.setFieldValue('postcode', '');
      formik.setFieldError('postcode', '');
      formik.setFieldValue('locality', '');
      formik.setFieldError('locality', '');
      formik.setFieldValue('province', '');
      formik.setFieldError('province', '');
      formik.setFieldValue('national_id', '');
      formik.setFieldError('national_id', '');
      formik.setFieldValue('complete_name', '');
      formik.setFieldError('complete_name', '');
      formik.setFieldValue('complete_name_chinese', '');
      formik.setFieldError('complete_name_chinese', '');
      formik.setFieldValue('address_line_1', '');
      formik.setFieldError('address_line_1', '');
      formik.setFieldValue('bank_card_number', '');
      formik.setFieldError('bank_card_number', '');
    }
  };

  const handleChangeDate = (date: dayjs.Dayjs) => {
    formik.setFieldValue('date_of_birth', date ? date.format('YYYY-MM-DD').toString() : '');
    formik.setFieldError('date_of_birth', '');
  };

  const getDisabledDate = (date: Dayjs) => date.isAfter() && !date.isSame(dayjs(), 'day');
  return (
    <OnboardingWrapper
      title={
        title ||
        `${person_details_key ? capitalize(person_details_key) : 'Individual'} ${
          person_details_key ? (index || index === 0) && index + 1 : ''
        } details`
      }
      titleHint={titleHint}
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col gap-8">
        {formik.values.country !== 'CHN' && (
          <>
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <Select
                isClearable
                label="gender"
                options={genderOptions}
                value={formik.values.gender}
                onChange={value => handleChangeSelect('gender', value || '')}
                error={formik.errors.gender}
              />
              <Select
                label="title"
                options={titleOptions}
                isClearable
                value={formik.values.title}
                defaultValue={formik.values.title}
                onChange={value => handleChangeSelect('title', value || '')}
                error={formik.errors.title}
              />
              <TextInputField label="first name" name={`first_name`} />
            </div>
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField label="Middle name (optional)" name={`middle_name`} />
              <TextInputField label="Last name" name={`last_name`} />
            </div>
          </>
        )}
        <CalendarInput
          label="date of birth"
          error={formik.errors.date_of_birth}
          defaultValue={
            formik.values.date_of_birth ? dayjs(formik.values.date_of_birth) : undefined
          }
          onChange={handleChangeDate}
          disabledDate={getDisabledDate}
        />
        <Select
          isClearable
          label="country"
          error={formik.errors.country}
          options={countryOptions}
          value={formik.values.country}
          onChange={value => handleChangeSelect('country', value || '')}
        />
        {formik.values.country === 'USA' && (
          <TextInputField name="social_security_number" label="social security number" />
        )}
        {formik.values.country === 'CHN' && (
          <>
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField label="Full name (Pinyin)" name="complete_name" />
              <TextInputField label="Full name (Chinese)" name="complete_name_chinese" />
            </div>
            <Select
              isClearable
              label="Gender"
              options={genderOptions.filter(
                option => option.value === 'male' || option.value === 'female',
              )}
              value={formik.values.gender}
              onChange={value => handleChangeSelect('gender', value || '')}
              error={formik.errors.gender}
            />
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField label="Address line 1" name="address_line_1" />
              <TextInputField label="Bank card number" name="bank_card_number" />
            </div>
          </>
        )}
        {isIndividualCountryWithNationalId(formik.values.country) && (
          <TextInputField
            name="national_id"
            label={`${getNationalIdDetails(formik.values.country).label} (${
              getNationalIdDetails(formik.values.country).formatHint
            })${formik.values.country === 'CHN' ? '' : ' (optional)'}`}
            placeholder={getNationalIdDetails(formik.values.country).placeholder}
            limit={getNationalIdDetails(formik.values.country).maxLength}
          />
        )}
        {formik.values.country !== 'CHN' && (
          <>
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField label="unit number (optional)" name="unit_number" />
              <TextInputField label="street number" name="street_number" />
            </div>
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField label="street Name" name="street_name" />
              <Select
                isClearable
                label={formik.values.country === 'AUS' ? 'Street Type' : 'Street Type (optional)'}
                key={`street_type_country_${formik.values.country}`}
                error={formik.errors.street_type}
                options={formik.values.country === 'AUS' ? streetTypeOptions : streetTypeOptionsUSA}
                value={formik.values.street_type}
                onChange={value => handleChangeSelect('street_type', value || '')}
                positionMenu="up"
              />
            </div>
          </>
        )}
        {isIndividualCountryGroup1(formik.values.country) && (
          <div className="w-full flex items-start">
            <Select
              isClearable
              key={`locality_country_${formik.values.country}`}
              label="Locality"
              options={localityOptions}
              value={formik.values.locality}
              onChange={value => handleChangeSelect('locality', value || '')}
              error={formik.errors.locality}
              positionMenu="up"
            />
          </div>
        )}
        {isIndividualCountryWithLocality(formik.values.country) &&
          !isIndividualCountryGroup1(formik.values.country) && (
            <TextInputField
              label="Locality"
              name="locality"
              placeholder={getLocalityExample(formik.values.country)}
            />
          )}
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          {formik.values.country !== 'CHN' && <TextInputField label="suburb" name="suburb" />}
          {(formik.values.country === 'AUS' || formik.values.country === 'USA') && (
            <Select
              isClearable
              label="State"
              key={`state_country_${formik.values.country}`}
              options={formik.values.country === 'AUS' ? auStateOptions : usaStateOptions}
              value={formik.values.state}
              onChange={value => handleChangeSelect('state', value || '')}
              error={formik.errors.state}
              positionMenu="up"
            />
          )}
          {(isIndividualCountryGroup3(formik.values.country) ||
            formik.values.country === 'CHN') && (
            <TextInputField
              label={formik.values.country === 'CHN' ? 'Province' : 'Province (optional)'}
              name="province"
            />
          )}
          <TextInputField
            label="postcode"
            name="postcode"
            placeholder={getPostcodeExample(formik.values.country)}
          />
        </div>
      </div>
    </OnboardingWrapper>
  );
};
